<template>
  <dashboard-widget class="leads-over-time"
    :is-loading="isLoading"
    is-loading-spinner>
    <template>
      <div class="dark-charts m-bottom-base">
        <h4 class="">{{ $t('ui.dashboard.leads_over_time.label') }}</h4>
        <div @mouseleave="setItVisibleTooltip(false)" :id="identifier" :ref="identifier" style="height: 182px"></div>
      </div>

      <div class="dashboard-tooltip" :style="tooltip">
        <span class="new">{{ chart.tooltip.leads }} {{ $t('ui.dashboard.leads_over_time.linear') }}</span> <br/>
        <span class="cumulated">{{ chart.tooltip.cumulative }} {{ $t('ui.dashboard.leads_over_time.cumulated') }}</span> <br/>
        <span class="date">{{ chart.tooltip.date | format('en', filter.period) }}</span>
      </div>

      <!-- @todo Merge into mother (LeadsOverTimeWidget) component -->
      <leads-collected-widget :filter="filter"/>
    </template>
  </dashboard-widget>
</template>

<script>
import fp from 'lodash/fp'
import head from 'lodash/head'
import last from 'lodash/last'
import reverse from 'lodash/fp/reverse'
import flow from 'lodash/flow'
import get from 'lodash/get'
import constant from 'lodash/constant'
import cond from 'lodash/cond'
import eq from 'lodash/eq'
import defaultTo from 'lodash/defaultTo'
import stubTrue from 'lodash/stubTrue'
import Plotly from 'plotly.js-dist'
import moment from 'moment'

import Charts from '@/api/Charts.js'
import WidgetMixin from './mixins/WidgetMixin'
import LeadsCollectedWidget from './LeadsCollectedWidget'
import WidgetQueryMixin from './mixins/WidgetQueryMixin'
import WidgetDateMixin from './mixins/WidgetDateMixin'

const each = fp.each.convert({ cap: false })

const getDefaultTrace = () => ({
  x: [],
  y: []
})

const AXIS_LAYOUT = {
  fixedrange: true,
  autorange: true,
  showgrid: false,
  rangemode: 'tozero'
}

// See: https://bl.ocks.org/zanarmstrong/raw/ca0adb7e426c12c06a95/
const DEFAULT_D3_DATE_FORMAT = '%d %b %Y'
const D3_HOUR_DATE_FORMAT = '%H:%M'

// See: https://momentjs.com/docs/#/displaying/format/
const DEFAULT_MOMENT_DATE_FORMAT = 'YYYY-MM-DD'
const MOMENT_HOUR_DATE_FORMAT = 'YYYY-MM-DD HH:mm'

export default {
  mixins: [WidgetMixin, WidgetQueryMixin, WidgetDateMixin],

  components: {
    LeadsCollectedWidget
  },

  filters: {
    // Formats the date according to period. It shows hours when period is equal zero. It doesn't show
    // hours when period is not equal zero
    format (date, language = 'en', period) {
      let format = DEFAULT_MOMENT_DATE_FORMAT
      if (period === '0') {
        format = MOMENT_HOUR_DATE_FORMAT
      }

      return moment(date).locale(language).format(format)
    }
  },

  props: {
    identifier: {
      type: String,
      default: 'leads-over-time'
    }
  },

  data: () => ({
    chart: {
      configuration: {
        staticPlot: false,
        responsive: true,
        displayModeBar: false
      },

      layout: {
        hovermode: 'x',
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(0,0,0,0)',
        showlegend: false,
        xaxis: {
          showspikes: false,
          spikedash: 'solid',

          // Determines the drawing mode for the spike line. If "toaxis", the line is drawn from the data point to the
          // axis the series is plotted on. If "across", the line is drawn across the entire plot area, and supercedes
          // "toaxis". If "marker", then a marker dot is drawn on the axis the series is plotted on
          spikemode: 'toaxis',

          // Determines whether spikelines are stuck to the cursor or to the closest datapoints
          spikesnap: 'data',
          spikethickness: 1, // 1 pixel
          spikedistance: 50, // 50 pixels
          hoverdistance: 50, // 50 pixels
          tickfont: {
            family: 'Poppins, sans-serif',
            color: '#9198AB'
          },
          ...AXIS_LAYOUT
        },

        yaxis: {
          tickfont: {
            family: 'Poppins, sans-serif',
            color: '#0263CA'
          },

          tickformat: ',d',
          ...AXIS_LAYOUT
        },

        yaxis2: {
          tickfont: {
            family: 'Poppins, sans-serif',
            color: '#9198AB'
          },

          side: 'right',
          overlaying: 'y',
          ...AXIS_LAYOUT
        },

        margin: {
          l: 30,
          r: 30,
          b: 20,
          t: 5,
          pad: 10
        }
      },

      linear: getDefaultTrace(),
      cumulated: getDefaultTrace(),
      total: 0,
      tooltip: {
        display: 'none',
        top: 0,
        left: 0,
        cumulative: 0,
        leads: 0,
        date: new Date()
      }
    }
  }),

  computed: {
    // CSS Style for dynamic tooltip presentation
    tooltip: {
      get () {
        return {
          display: this.chart.tooltip.display,
          left: `${this.chart.tooltip.left}px`,
          top: `${this.chart.tooltip.top}px`
        }
      }
    },

    // Represents the tick format for a Plotly.js axis. It shows hours when period is equal zero. It
    // doesn't show hours when period is not equal zero
    tickFormat: {
      get () {
        if (this.filter.period === '0') {
          return D3_HOUR_DATE_FORMAT
        }

        return DEFAULT_D3_DATE_FORMAT
      }
    }
  },

  methods: {
    async refresh() {
        this.setIsLoading(true)

        try {
            const leadsOverTime = await this.get(this.filter.period, this.filter.projectId)

            this.chart.linear = getDefaultTrace()
            this.chart.cumulated = getDefaultTrace()

            this.transform(leadsOverTime)
        } catch (error) {
            this.setError(error)
            this.setIsError(true)
        } finally {
            this.plot()

            this.setIsLoading(false)
            this.eventBus.$emit('widget-loaded', this.identifier)
        }
    },

    async get (period, project) {
      return new Promise(async (resolve, reject) => {
        let charts = new Charts()

        let tuples = [
          { key: 'daysAgo', value: period }
        ]

        if (!!project) {
          tuples.push({ key: 'projectId', value: project })
        }

        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        tuples.push({ key: 'inTimeZone', value: `"${ userTimeZone }"` })

        const querySchema = `
          count
          dateTimeStep
        `
        const query = this.getQuery(tuples, 'leadsCountPerPeriod', querySchema)
        let { leads_count_per_period: leadsOverTime } = await charts.postQuery({
          organization_id: this.filter.organizationId,
          operationName: null,
          variables: {},
          query: `query Query {
              ${query}
            }`
        })
        .catch(reject)

        resolve(leadsOverTime)
      })
    },

    setItVisibleTooltip (isVisibleTooltip) {
      this.chart.tooltip.display = isVisibleTooltip ? 'block' : 'none'
    },

    plot () {
      const plot = Plotly.newPlot(
        this.identifier,
        [
          {
            name: this.$t('ui.dashboard.leads_over_time.linear'),
            yaxis: 'y2',
            x: this.chart.linear.x,
            y: this.chart.linear.y,
            line: {
              color: '#C4CDD5',
              shape: 'linear'
            },

            fill: 'tozeroy',
            fillcolor: 'rgba(255, 255, 247, 0.05)',
            type: 'scatter',
            mode: 'lines+markers',
            hoverinfo: 'none'
          },
          {
            name: this.$t('ui.dashboard.leads_over_time.cumulated'),
            x: this.chart.cumulated.x,
            y: this.chart.cumulated.y,
            line: {
              color: '#2487f7',
              shape: 'spline'
            },

            fill: 'tozeroy',
            fillcolor: 'rgba(36, 135, 247, 0.15)',
            type: 'scatter',
            mode: 'lines',
            hoverinfo: 'none'
          }
        ],
        {
          ...this.chart.layout,
          xaxis: {
            tickformat: this.tickFormat,
            ...this.chart.layout.xaxis
          },
        },
        this.chart.configuration
      )

      // To force responsiveness
      window.dispatchEvent(new Event('resize'))

      // Show tooltip on hover
      this.$refs[this.identifier].on('plotly_hover', ({ points, event }) => {
        this.chart.tooltip.left = event.clientX
        this.chart.tooltip.top = event.clientY

        const leads = head(points)
        const cumulative = last(points)

        this.chart.tooltip.leads = leads.y
        this.chart.tooltip.cumulative = cumulative.y
        this.chart.tooltip.date = leads.x

        this.chart.tooltip.display = 'block'
      })
    },

    // @todo Return instead of mutate global state
    transform (leadsOverTime) {
      flow([
        reverse,
        each(({ dateTimeStep, count }, index) => {
          this.chart.linear.x.push(dateTimeStep)
          this.chart.linear.y.push(count)

          const previous = defaultTo(get(this.chart.cumulated.y, index - 1), 0)
          this.chart.cumulated.x.push(dateTimeStep)
          this.chart.cumulated.y.push(previous + count)
        })
      ])(leadsOverTime)
    }
  }
}
</script>

<style lang="sass">
.float-left
  float: left
  padding-right: 5px

.dark-charts
  h4
    color: $paper
    margin-top: 0px
    margin-bottom: 10px

.leads-over-time
  min-height: 304px
  svg.main-svg
      overflow: visible !important

.dashboard-tooltip
  background: rgba(33, 39, 54, 0.8)
  position: fixed
  max-width: 350px
  border-radius: 4px
  padding: 10px 15px
  z-index: 1
  span
    font-size: 14px
    line-height: 20px
    font-weight: $font-medium
    &.new
      color: $paper-lighter
    &.cumulated
      color: $sensual
    &.date
      color: $ink-lighter
      font-size: 12px

</style>



// WEBPACK FOOTER //
// LeadsOverTimeWidget.vue?c5d04e54